import React, { useState, useEffect } from 'react';

export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return { toasts, showToast, removeToast };
};

export const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <Toast 
          key={toast.id} 
          toast={toast} 
          onClose={() => removeToast(toast.id)} 
        />
      ))}
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Otomatik kapanmadan 300ms önce çıkış animasyonunu başlat
    const timer = setTimeout(() => {
      setIsExiting(true);
    }, toast.duration - 300);

    return () => clearTimeout(timer);
  }, [toast.duration]);

  const getToastStyles = () => {
    const baseStyles = {
      padding: '16px 20px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '320px',
      maxWidth: '500px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      border: '1px solid',
      pointerEvents: 'all',
      cursor: 'pointer',
      animation: isExiting 
        ? 'toastSlideOut 0.3s ease-out forwards' 
        : 'toastSlideIn 0.3s ease-out',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    };

    const typeStyles = {
      success: {
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(5, 150, 105, 0.25))',
        borderColor: 'rgba(16, 185, 129, 0.5)',
        color: '#d1fae5',
      },
      error: {
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.25))',
        borderColor: 'rgba(239, 68, 68, 0.5)',
        color: '#fee2e2',
      },
      warning: {
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.25), rgba(217, 119, 6, 0.25))',
        borderColor: 'rgba(245, 158, 11, 0.5)',
        color: '#fef3c7',
      },
      info: {
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.25))',
        borderColor: 'rgba(59, 130, 246, 0.5)',
        color: '#dbeafe',
      },
    };

    return { ...baseStyles, ...typeStyles[toast.type] };
  };

  const getIcon = () => {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
    };
    return icons[toast.type] || icons.info;
  };

  return (
    <>
      <style>{`
        @keyframes toastSlideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes toastSlideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }
      `}</style>
      
      <div 
        style={getToastStyles()}
        onClick={onClose}
      >
        <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
          {getIcon()}
        </span>
        <span style={{ 
          flex: 1, 
          fontSize: '0.95rem',
          fontWeight: '500',
          lineHeight: '1.4',
        }}>
          {toast.message}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            padding: '4px 8px',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '600',
            transition: 'all 0.2s',
            flexShrink: 0,
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          ✕
        </button>
      </div>
    </>
  );
};